import React, { useEffect, useState } from "react";
import { Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const DASHBOARD_TYPOGRAPHY = {
  sectionTitle: "text-xs font-semibold uppercase tracking-[0.18em] text-white/65",
  body: "text-sm font-medium text-white",
  fine: "text-xs text-white/60",
};

const AUTOMATION_STATUS_CLASSNAMES = {
  executed: "border-emerald-500/30 bg-emerald-500/15 text-emerald-300",
  queued: "border-amber-500/30 bg-amber-500/15 text-amber-200",
  queued_close: "border-amber-500/30 bg-amber-500/15 text-amber-200",
  awaiting_broker_confirmation: "border-sky-500/30 bg-sky-500/15 text-sky-200",
  no_signal: "border-white/10 bg-white/5 text-white/70",
  duplicate_direction: "border-violet-500/30 bg-violet-500/15 text-violet-200",
  holding: "border-violet-500/30 bg-violet-500/15 text-violet-200",
  signal_only: "border-cyan-500/30 bg-cyan-500/15 text-cyan-200",
  blocked: "border-rose-500/30 bg-rose-500/15 text-rose-200",
  failed: "border-rose-500/30 bg-rose-500/15 text-rose-200",
  error: "border-rose-500/30 bg-rose-500/15 text-rose-200",
};

const formatRunStatusLabel = (value) => String(value || "unknown")
  .split("_")
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(" ");

const formatTriggerSourceLabel = (value) => {
  if (!value) return null;
  if (value === "persistent_state_entry") return "Persistent/Resume Entry";
  if (value === "signal_flip_resume") return "Signal Flip Resume";
  if (value === "session_resume_state") return "Session Resume";
  if (value === "current_state_resume") return "Current State Resume";
  return String(value)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const REGIME_CLASSNAMES = {
  BULLISH: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  BEARISH: "border-rose-500/40 bg-rose-500/10 text-rose-300",
  NEUTRAL: "border-white/15 bg-white/5 text-white/70",
};

const regimeLabel = (state) => {
  if (!state) return null;
  const s = String(state).toUpperCase();
  if (s === "BULLISH") return "↑ Bullish";
  if (s === "BEARISH") return "↓ Bearish";
  return "→ Neutral";
};

const describeLatestAutomationRun = (run, symbolLabel) => {
  if (!run) {
    return {
      headline: null,
      detail: null,
      trigger: null,
    };
  }

  const summary = run.summary || {};
  const normalizedStatus = String(run.status || "").toLowerCase();
  const action = String(summary.action || "").toUpperCase();
  const state = String(summary.state || "").toUpperCase();
  const trigger = formatTriggerSourceLabel(summary.trigger_source);
  if (summary.reason_code === 'AI_RESEARCH_INTERVAL_WAIT') {
    return { headline: 'Waiting for the next scheduled evaluation',
      detail: `The AI evaluates this account every ${summary.interval_minutes || 5} minutes. No broker submission is pending during this interval.`, trigger };
  }
  if (summary.reason_code === 'AI_RESEARCH_CONTEXT_TOO_LARGE') {
    return { headline: 'AI context too large — no order sent',
      detail: 'The request could not fit safely in the model context. Review the AI context settings; account risk facts are never discarded.', trigger };
  }
  if (summary.reason_code === 'AI_RESEARCH_RISK_BUDGET_UNAVAILABLE') {
    return { headline: 'No positive entry risk budget',
      detail: 'The configured percentage of the broker balance gives zero available risk. Review the account capital basis and risk settings; no order is pending.', trigger };
  }
  if (summary.reason_code === 'BROKER_ACCOUNTING_UNAVAILABLE') {
    return { headline: 'Broker accounting unavailable — entries paused',
      detail: 'Current session P&L could not be verified with the broker. Position protection remains active.', trigger };
  }
  if (summary.reason_code === 'AI_RESEARCH_MODEL_INVALID') {
    return { headline: 'AI response invalid — no order sent',
      detail: 'The model returned an incomplete or contradictory decision. Provider diagnostics require attention; this is not a broker rejection.', trigger };
  }
  if (summary.reason_code === 'AI_GUIDED_PROVIDER_FAILED') {
    return { headline: 'AI providers unavailable — no order sent',
      detail: 'Check the AI provider connection, quota and response time in admin settings.', trigger };
  }
  if (summary.reason_code === 'ACTIVE_BROKER_POSITION') {
    return { headline: 'Broker position open — new entry paused',
      detail: 'The account already has broker exposure. Position management remains active; another entry waits for reconciliation.', trigger };
  }
  if (summary.reason_code === 'RISK_SETTINGS_MISSING') {
    return { headline: 'Account risk limits have not been saved',
      detail: 'Open Settings → Risk Management, select this account, review the limits and save them. Displayed defaults are not saved limits.', trigger };
  }
  if (summary.reason_code === 'AI_RESEARCH_MODE_ACTIVE') {
    return { headline: 'AI is the active entry engine',
      detail: 'Classic strategy entries are paused. Select Strategy mode to use strategy signals; existing positions keep their protection.', trigger };
  }

  if (normalizedStatus === "queued") {
    return {
      headline: `${action || "Signal"} queued${run.mode === "live" ? " to broker" : ""}`,
      detail: summary.reason || `Automation created a new ${symbolLabel || summary.symbol || "instrument"} entry and queued it for execution.`,
      trigger,
    };
  }
  if (normalizedStatus === "queued_close") {
    return {
      headline: "Exit queued to broker",
      detail: summary.reason || "Automation requested a close for the current position.",
      trigger,
    };
  }
  if (normalizedStatus === "awaiting_broker_confirmation") {
    return {
      headline: "Waiting for broker confirmation",
      detail: summary.reason || "A live order or position is still pending broker confirmation.",
      trigger,
    };
  }
  if (normalizedStatus === "duplicate_direction" || normalizedStatus === "holding") {
    return {
      headline: "Signal suppressed",
      detail: summary.reason || "Automation detected exposure that prevented a new entry.",
      trigger,
    };
  }
  if (normalizedStatus === "no_signal") {
    if (summary.reason_code === "WAITING_FOR_NEXT_SIGNAL_TRANSITION") {
      return {
        headline: "Waiting for next entry transition",
        detail: summary.reason_detail || summary.reason || `Strategy is currently ${state || "active"}, but automation requires a fresh transition before entering.`,
        trigger,
      };
    }
    return {
      headline: "No fresh entry signal",
      detail: state ? `Current strategy state is ${state} for ${symbolLabel || summary.symbol || "this scope"}.` : "The latest evaluation did not produce a new executable entry.",
      trigger,
    };
  }
  if (normalizedStatus === "signal_only") {
    return {
      headline: "Signal detected but not executed",
      detail: summary.reason || "Automation recorded the signal without opening a position.",
      trigger,
    };
  }
  if (normalizedStatus === "blocked" || normalizedStatus === "failed" || normalizedStatus === "error") {
    return {
      headline: "Execution blocked",
      detail: summary.reason || summary.error || "Automation could not execute this signal.",
      trigger,
    };
  }
  if (normalizedStatus === "executed") {
    return {
      headline: "Position opened",
      detail: summary.reason || "Automation executed the signal successfully.",
      trigger,
    };
  }

  return {
    headline: formatRunStatusLabel(run.status),
    detail: summary.reason || null,
    trigger,
  };
};

const AutoTradingControls = ({
  selectedTradingAccountId,
  autoTradeEnabledForSelectedAccount,
  toggleAutoTrading,
  paperModeForSelectedScope,
  togglePaperMode,
  displayedPaperMode,
  aiGuidedTradingEnabled,
  aiGuidedTradingAvailable,
  toggleAiGuidedTrading,
  aiResearchInstruments,
  aiResearchAllowLimitOrders,
  updateAiResearchSettings,
  autoTradeEnabled,
  autoTradeAccountIds,
  selectedAccountIsInAutoTradeScope,
  selectedScopeHasActiveLink,
  selectedInstrumentOption,
  symbol,
  latestAutomationRun,
  latestAutomationRunLoading,
  latestAutomationRunTime,
  goToAutoTradeSettings,
  goToStrategies,
  boundIntervalMinutes,
  currentIntervalMinutes,
  activeLinkId,
  contractsToTrade,
  contractsToTradeSaving,
  onSaveContractsToTrade,
}) => {
  const symbolLabel = selectedInstrumentOption?.label || symbol;
  const normalizedContractsToTrade = Math.max(1, Number(contractsToTrade) || 1);
  const [contractsDraft, setContractsDraft] = useState(String(normalizedContractsToTrade));
  const draftQuantity = Math.max(1, Number.parseInt(contractsDraft || "1", 10) || 1);
  const contractsDirty = draftQuantity !== normalizedContractsToTrade;
  const boundTimeframeLabel = Number.isFinite(Number(boundIntervalMinutes))
    ? `${Number(boundIntervalMinutes)}m`
    : null;
  const currentTimeframeLabel = Number.isFinite(Number(currentIntervalMinutes))
    ? `${Number(currentIntervalMinutes)}m`
    : null;
  const timeframeMismatch = Boolean(
    boundTimeframeLabel
    && currentTimeframeLabel
    && Number(boundIntervalMinutes) !== Number(currentIntervalMinutes),
  );
  const latestRunDescription = describeLatestAutomationRun(latestAutomationRun, symbolLabel);
  const latestRunStatusClassName = AUTOMATION_STATUS_CLASSNAMES[String(latestAutomationRun?.status || "").toLowerCase()]
    || "border-white/10 bg-white/5 text-white/70";

  useEffect(() => {
    setContractsDraft(String(normalizedContractsToTrade));
  }, [activeLinkId, normalizedContractsToTrade]);

  const handleSaveContracts = () => {
    if (!onSaveContractsToTrade) return;
    onSaveContractsToTrade(draftQuantity);
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader className="pb-2">
        <CardTitle className={`${DASHBOARD_TYPOGRAPHY.sectionTitle} flex items-center gap-2`}>
          <Zap className="h-4 w-4" />
          Auto Trading
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className={DASHBOARD_TYPOGRAPHY.body}>
            {selectedTradingAccountId ? "Enabled for this account + instrument" : "Enabled"}
          </span>
          <Switch checked={autoTradeEnabledForSelectedAccount} onCheckedChange={toggleAutoTrading} />
        </div>
        <div className="flex items-center justify-between">
          <span className={DASHBOARD_TYPOGRAPHY.body}>Paper Mode</span>
          <Switch
            checked={paperModeForSelectedScope}
            onCheckedChange={togglePaperMode}
            disabled={selectedTradingAccountId ? !autoTradeEnabledForSelectedAccount : false}
          />
        </div>
        <p className={DASHBOARD_TYPOGRAPHY.fine}>
          {displayedPaperMode
            ? "Paper mode keeps trades local. Signals will not open positions in Topstep."
            : "Live mode queues broker orders to Topstep. Watch execution status until the broker confirms the fill."}
        </p>
        <div className="rounded-md border border-white/10 bg-black/20 px-3 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className={DASHBOARD_TYPOGRAPHY.body}>Entry engine: {aiGuidedTradingEnabled ? 'AI Research Trader' : 'Strategy signals'}</div>
              <div className={DASHBOARD_TYPOGRAPHY.fine}>
                One entry engine owns this account. Strategy mode follows linked strategies; AI mode pauses their entries. Both retain broker and risk checks.
              </div>
            </div>
            <select aria-label="Entry engine" value={aiGuidedTradingEnabled ? 'ai' : 'strategy'}
              disabled={!selectedTradingAccountId}
              className="bg-zinc-900 border border-zinc-600 rounded px-2 py-1 text-sm"
              onChange={event => toggleAiGuidedTrading(event.target.value === 'ai')}>
              <option value="strategy">Strategy</option>
              <option value="ai" disabled={!aiGuidedTradingAvailable}>AI Research Trader</option>
            </select>
          </div>
          {!aiGuidedTradingAvailable ? (
            <div className="mt-1 text-[10px] text-amber-300/75">
              The administrator has not enabled an AI trading provider.
            </div>
          ) : null}
          {aiGuidedTradingEnabled ? (
            <div className="mt-3 space-y-3 border-t border-white/10 pt-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55">Allowed micro instruments</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {["MES", "MNQ", "MCL", "MGC", "SIL", "M2K"].map((instrument) => {
                    const selected = (aiResearchInstruments || []).includes(instrument);
                    return (
                      <button
                        key={instrument}
                        type="button"
                        onClick={() => {
                          const next = selected
                            ? (aiResearchInstruments || []).filter((item) => item !== instrument)
                            : [...(aiResearchInstruments || []), instrument];
                          if (next.length) updateAiResearchSettings?.({ instruments: next });
                        }}
                        className={`rounded border px-2.5 py-1 text-xs font-medium ${selected ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" : "border-white/10 bg-white/5 text-white/55"}`}
                      >
                        {instrument}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-medium text-white/85">Allow limit entries</div>
                  <div className="text-[10px] text-white/45">Market orders remain available; unfilled limits are still managed by broker truth.</div>
                </div>
                <Switch
                  checked={Boolean(aiResearchAllowLimitOrders)}
                  onCheckedChange={(checked) => updateAiResearchSettings?.({ allow_limit_orders: checked })}
                />
              </div>
            </div>
          ) : null}
        </div>
        <div className="rounded-md border border-white/10 bg-black/20 px-3 py-2 text-xs text-white/72">
          {!autoTradeEnabled
            ? "Auto-trade is currently off. Enable it for the account you want to automate."
            : autoTradeAccountIds.length === 0
              ? "No trading accounts are selected for auto-trade. Choose them in Settings."
              : aiGuidedTradingEnabled
                ? selectedTradingAccountId && !selectedAccountIsInAutoTradeScope
                  ? "This account is outside the enabled automation scope, so the AI Research Trader will not act on it."
                  : "AI Research Trader is active for enabled accounts and the selected micro-instrument allowlist. Strategy signals do not create entries in this mode."
              : selectedTradingAccountId
                ? !selectedAccountIsInAutoTradeScope
                  ? "This account is excluded from the current auto-trade scope."
                  : selectedScopeHasActiveLink
                    ? `This account + ${symbolLabel} are active for auto-trade.`
                    : `This account is in scope, but ${symbolLabel} does not have an active linked strategy.`
                : `${autoTradeAccountIds.length} account(s) are currently selected for auto-trade.`}
        </div>
        {autoTradeEnabledForSelectedAccount && boundTimeframeLabel ? (
          <div
            className={`rounded-md border px-3 py-2 text-xs ${
              timeframeMismatch
                ? "border-sky-500/30 bg-sky-500/10 text-sky-200"
                : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
            }`}
          >
            <div className="font-semibold">
              Entry signal bound to {symbolLabel} on {boundTimeframeLabel}
            </div>
            {timeframeMismatch ? (
              <div className="mt-1 text-sky-100/80">
                Chart is showing {currentTimeframeLabel}. Entries still wait for {boundTimeframeLabel} signals from the linked strategy.
                Stops and take-profit mechanisms run on the risk timeframes selected in Settings.
              </div>
            ) : (
              <div className="mt-1 text-emerald-200/80">
                Entries follow this signal timeframe. Stops and take-profit mechanisms can use their own risk timeframes.
              </div>
            )}
          </div>
        ) : null}

        {selectedTradingAccountId && selectedScopeHasActiveLink ? (
          <div className="rounded-md border border-white/10 bg-black/20 px-3 py-2.5">
            <div className="flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/65">
                  Contracts to trade
                </label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={contractsDraft}
                  onChange={(event) => setContractsDraft(event.target.value)}
                  onBlur={() => setContractsDraft(String(draftQuantity))}
                  className="mt-1 h-9 bg-white/5 border-white/10 text-white"
                />
              </div>
              <Button
                type="button"
                size="sm"
                onClick={handleSaveContracts}
                disabled={!contractsDirty || contractsToTradeSaving || !onSaveContractsToTrade}
                className="h-9"
              >
                {contractsToTradeSaving ? "Saving..." : "Save"}
              </Button>
            </div>
            <div className="mt-1 text-[10px] text-white/45">
              Applies to the next new automation entry only. Existing open positions are not resized.
            </div>
          </div>
        ) : null}

        {selectedTradingAccountId ? (
          <div
            data-testid="last-automation-decision"
            className="h-[260px] overflow-y-auto overscroll-contain rounded-md border border-white/10 bg-black/20 px-3 py-2.5"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/65">
                  Last automation decision
                  {latestAutomationRun && latestAutomationRunTime
                    ? <span className="ml-1 normal-case tracking-normal text-white/45">as of {latestAutomationRunTime}</span>
                    : null}
                </div>
                <div className="mt-1 text-sm font-medium text-white">
                  {latestAutomationRunLoading && !latestAutomationRun
                    ? "Checking automation status..."
                    : latestRunDescription.headline || "No scoped automation run recorded yet"}
                </div>
              </div>
              {latestAutomationRun ? (
                <div className="flex items-center gap-2">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] ${latestRunStatusClassName}`}>
                    {formatRunStatusLabel(latestAutomationRun.status)}
                  </span>
                  <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/60">
                    {latestAutomationRun.mode || "scan"}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="mt-2 text-xs text-white/60">
              {latestAutomationRun
                ? (latestRunDescription.detail || "Automation recorded a scoped evaluation for this account and instrument.")
                : (latestAutomationRunLoading
                  ? "Waiting for the latest scoped evaluation to load."
                  : `No automation run has been recorded yet for this account + ${symbolLabel} scope.`)}
            </div>

            {latestAutomationRun ? (
              <div className="mt-1 text-[10px] text-white/40">
                This is the last persisted automation decision and may differ from the live chart overlay.
              </div>
            ) : null}

            {latestAutomationRun ? (
              <>
                {/* Regime badge row */}
                {latestAutomationRun.summary?.signal_state ? (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">Regime</span>
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] ${REGIME_CLASSNAMES[latestAutomationRun.summary.signal_state] || REGIME_CLASSNAMES.NEUTRAL}`}>
                      {regimeLabel(latestAutomationRun.summary.signal_state)}
                    </span>
                    {boundTimeframeLabel && (
                      <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white/70">
                        {boundTimeframeLabel}
                      </span>
                    )}
                    {latestAutomationRun.summary.signal_state !== "NEUTRAL" && (
                      <span className="text-[10px] text-white/30">
                        state — not a crossover event
                      </span>
                    )}
                  </div>
                ) : null}

                {/* Persistent entry result — only show if attempted */}
                {latestAutomationRun.summary?.persistent_entry_attempted && latestAutomationRun.summary?.persistent_entry_reason_code ? (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">Resume Entry</span>
                    <span className="text-[10px] text-amber-300/80">
                      Skipped — {String(latestAutomationRun.summary.persistent_entry_reason_code).replaceAll("_", " ").toLowerCase()}
                    </span>
                  </div>
                ) : null}
                {latestAutomationRun.summary?.trigger_source === "persistent_state_entry" ? (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-400/70">Resume Entry</span>
                    <span className="text-[10px] text-emerald-300/80">
                      Entered — current regime still valid
                    </span>
                  </div>
                ) : null}

                {/* Metadata row */}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-white/65">
                  {latestAutomationRunTime ? <span>Checked {latestAutomationRunTime}</span> : null}
                  {latestAutomationRun.summary?.decision ? <span>Decision: {String(latestAutomationRun.summary.decision).toUpperCase()}</span> : null}
                  {latestAutomationRun.summary?.reason_code ? <span>Reason: {String(latestAutomationRun.summary.reason_code).toUpperCase()}</span> : null}
                  {latestRunDescription.trigger ? <span>Trigger: {latestRunDescription.trigger}</span> : null}
                  {latestAutomationRun.summary?.action ? <span>Action: {String(latestAutomationRun.summary.action).toUpperCase()}</span> : null}
                  {!latestAutomationRun.summary?.action && latestAutomationRun.summary?.state ? (
                    <span>State: {String(latestAutomationRun.summary.state).toUpperCase()}</span>
                  ) : null}
                  {latestAutomationRun.summary?.is_fresh_signal !== undefined ? (
                    <span>{latestAutomationRun.summary.is_fresh_signal ? "Fresh transition" : "No fresh transition"}</span>
                  ) : null}
                  {latestAutomationRun.summary?.automation_requires_fresh_transition !== undefined ? (
                    <span>{latestAutomationRun.summary.automation_requires_fresh_transition ? "Requires fresh transition" : "Current-state resume allowed"}</span>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        <div className="flex gap-2">
          <Button
            onClick={goToAutoTradeSettings}
            variant="outline"
            size="sm"
            className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10"
          >
            Settings
          </Button>
          <Button
            onClick={goToStrategies}
            variant="outline"
            size="sm"
            className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10"
          >
            Strategies
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default React.memo(AutoTradingControls);
